import type { ActivityDetail } from '@painpal/contracts';
import { useMemo, useState } from 'react';
import { useIsPhone } from '../../ui/useMediaQuery';
import { fastestLabel, kmSplits, splitBars, splitsWidthPx, type SplitBar } from './splits';
import { useStreams } from './streams';
import styles from './splits.module.css';

export const SPLIT_TRACK_PX = { desktop: 250, phone: 170 } as const;

interface SplitColumnsProps { bars: SplitBar[]; track: number; isPhone: boolean; picked: number | null; onPick: (km: number) => void }

// Sized from the split count so every column fits its labels; past the card width the columns scroll sideways.
function SplitColumns({ bars, track, isPhone, picked, onPick }: SplitColumnsProps) {
  return (
    <div className={styles.scroll}>
      <div className={styles.scrollInner} style={{ minWidth: splitsWidthPx(bars.length, isPhone ? 'phone' : 'desktop') }}>
        <ol className={styles.bars} style={{ height: track }} aria-label="Kilometres">
          {bars.map((bar) => (
            <li key={bar.km} className={styles.split}>
              <button type="button" className={styles.splitButton} aria-label={bar.label} aria-pressed={bar.km === picked} onClick={() => onPick(bar.km)}>
                <span className={styles.pace} aria-hidden="true">{bar.pace}</span>
                <span className={styles.bar} style={{ height: bar.heightPx, background: bar.color }} />
              </button>
            </li>
          ))}
        </ol>
        <div className={styles.labels} aria-hidden="true">
          {bars.map((bar) => (
            <span key={bar.km} className={styles.label}>
              <span className={styles.km}>{isPhone ? String(bar.km) : `KM ${bar.km}`}</span>
              {!isPhone && <span className={styles.gap}>{`GAP ${bar.gap}`}</span>}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SplitsCard({ activity, url }: { activity: ActivityDetail; url: string }) {
  const streams = useStreams(activity.id, url);
  const isPhone = useIsPhone();
  const [picked, setPicked] = useState<number | null>(null);
  const splits = useMemo(() => (streams.data === undefined ? [] : kmSplits(streams.data)), [streams.data]);
  if (splits.length === 0) return null;
  const track = SPLIT_TRACK_PX[isPhone ? 'phone' : 'desktop'];
  const bars = splitBars(splits, activity.zones.pace?.bounds ?? null, track);
  const chosen = bars.find((bar) => bar.km === picked);
  return (
    <section className={styles.card} aria-labelledby="splits-title">
      <header className={styles.head}>
        <div className={styles.headText}>
          <h2 id="splits-title" className={styles.title}>KM SPLITS</h2>
          <p className={styles.sub}>{isPhone ? 'TALLER IS FASTER · COLOUR = PACE ZONE' : 'BAR = PACE, COLOURED BY PACE ZONE · TALLER IS FASTER · GAP UNDER EACH KM'}</p>
        </div>
        {!isPhone && <span className={styles.fastest}>{fastestLabel(splits, false)}</span>}
      </header>
      <SplitColumns bars={bars} track={track} isPhone={isPhone} picked={picked} onPick={(km) => setPicked((current) => (current === km ? null : km))} />
      {isPhone && (
        <p className={styles.note} role="status">
          {chosen === undefined ? `TAP A KM FOR ITS GRADE-ADJUSTED PACE · ${fastestLabel(splits, true)}` : `KM ${chosen.km} · ${chosen.pace} /KM · GAP ${chosen.gap} /KM`}
        </p>
      )}
    </section>
  );
}
